import { TEMPLATE_SIZE, TEMPLATE_BOUNDS } from './video-utils';
import { AUTO_FONT_RANGE } from '../text';
import { resolveColorBoundary } from '../text/colorBoundary';

export interface WrapTextLinesInput {
  text: string;
  fontSize: number | null;
  fontFamily: string;
  fontVariationSettings?: Record<string, number>;
  uppercase: boolean;
  bottom: number;        // template px (0..1920): template bottom -> box bottom edge
  previewWidth: number;  // px; scaleX = TEMPLATE_SIZE.width / previewWidth
  lineSpacing?: number;  // line-height ratio (default 1.0); affects box height only, not line breaks
  /**
   * Отступы плашки заголовка, preview-space px — ровно те же поля, что читает
   * drawOverlay. Перенос обязан считаться тем же отступом, каким рисуется бокс:
   * иначе строка ломается по одной ширине, а коробка рисуется по другой.
   */
  textBoxPadding?: number;
  textBoxPaddingTop?: number;
  textBoxPaddingLeft?: number;
  textBoxPaddingRight?: number;
  /**
   * Потолок авто-кегля, template px @1080. По-шаблонный: часть пабликов просит
   * «чуть меньше», kaztwitter — крупнее общего максимума, у остальных всё
   * устраивает. Заменяет AUTO_FONT_RANGE.max в обе стороны; на ручной
   * fontSize не влияет.
   */
  maxFontSize?: number;
  /**
   * Есть ли видимая плашка под текстом (backgroundAlpha > 0). С плашкой авто
   * стремится к «пропорциональной» форме — см. правило трёх строк ниже; текст
   * без подложки (qara.news, обводка) складывается столбиком, как и раньше.
   */
  plateVisible?: boolean;
  /**
   * Монтажёрское правило: крашеная часть (до двоеточия включительно) всегда
   * завершает строку — так в эталонах kaztwitter и kazpress. По-шаблонный
   * флаг, а не дефолт: alash, наоборот, держит продолжение в той же строке
   * (граница цвета проходит посреди строки).
   */
  colonBreak?: boolean;
  /**
   * Монтажёрская рабочая ширина текстовой зоны, template px @1080. У́же общей
   * зоны кадра: перенос происходит раньше, и блок держит эталонную форму —
   * kazpress ≈ 860 при полной зоне 992 (отзыв 04.09: «всё ещё не то», блок
   * шире монтажёрского при любом кегле). Не задана или 0 — полная зона.
   */
  textZoneWidth?: number;
}

export interface WrapTextLinesResult {
  lines: string[];
  fontSize: number;      // resolved (never null), preview-space px
  textBoxWidth: number;  // template px = max line width + padding*2
  /** Столько первых строк покрашены целиком первым цветом. */
  primaryLines: number;
  /**
   * Сколько символов строки primaryLines ещё покрашены первым цветом. Граница
   * проходит посреди строки — в макете Alash бирюза кончается на двоеточии,
   * а белый текст идёт дальше в той же строке. См. resolveColorBoundary.
   */
  splitChars: number;
}

export type MeasureFn = (text: string, font: string) => number;

function buildFontString(fontPx: number, fontFamily: string, fv?: Record<string, number>): string {
  const weight = fv?.wght ?? 'normal';
  const style =
    fv?.ital && fv.ital > 0.5
      ? 'italic'
      : fv?.slnt && fv.slnt !== 0
        ? `oblique ${Math.abs(fv.slnt)}deg`
        : 'normal';
  // Кавычки обязательны: имена кастомных шрифтов содержат точки/запятые
  // (напр. "CustomFont_ofont.ru_Impact_…") → без кавычек CSS-шорткат невалиден,
  // canvas игнорит присваивание и меряет дефолтными 10px → переносы уезжают.
  return `${style} ${weight} ${fontPx}px "${fontFamily || 'Arial'}", sans-serif`;
}

let _canvas: HTMLCanvasElement | null = null;
function canvasMeasure(): MeasureFn {
  return (text: string, font: string) => {
    if (typeof document === 'undefined' || typeof document.createElement !== 'function') {
      return text.length * 10; // headless / non-DOM environment
    }
    if (!_canvas) _canvas = document.createElement('canvas');
    const ctx = _canvas.getContext('2d');
    if (!ctx) return text.length * 10;
    ctx.font = font;
    return ctx.measureText(text).width;
  };
}

export function wrapTextLines(
  input: WrapTextLinesInput,
  measure: MeasureFn = canvasMeasure(),
): WrapTextLinesResult {
  // Нормализация: хвостовые пробелы, пустые строки и края не влияют на
  // раскладку — вставленный из чата заголовок часто тянет лишний Enter,
  // и плашка растёт из-за невидимого мусора.
  const cleaned = input.text
    .replace(/[ \t]+(\n|$)/g, '$1')
    .replace(/\n{2,}/g, '\n')
    .trim();
  let displayText = input.uppercase ? cleaned.toUpperCase() : cleaned;
  if (input.colonBreak) {
    // Принудительный перенос после крашеной части: считаем границу на тексте
    // со схлопнутыми пробелами (как ниже в wrap) и ставим '\n' вместо пробела.
    const joined = displayText
      .split('\n')
      .map((p) => p.split(' ').filter(Boolean).join(' '))
      .join('\n');
    const b = resolveColorBoundary(joined);
    if (b !== null && b < joined.length && joined[b] !== '\n') {
      displayText = joined[b] === ' '
        ? joined.slice(0, b) + '\n' + joined.slice(b + 1)
        : joined.slice(0, b) + '\n' + joined.slice(b);
    } else {
      displayText = joined;
    }
  }

  const scaleX = TEMPLATE_SIZE.width / input.previewWidth;
  const spacing = input.lineSpacing && input.lineSpacing > 0 ? input.lineSpacing : 1.0;

  const zoneCap = input.textZoneWidth && input.textZoneWidth > 0 ? input.textZoneWidth : Infinity;
  const FULL_WIDTH = Math.min(TEMPLATE_BOUNDS.right - TEMPLATE_BOUNDS.left, zoneCap);
  const NOTCH_WIDTH = Math.min(TEMPLATE_BOUNDS.rightCornerLeft - TEMPLATE_BOUNDS.left, zoneCap);
  const bottomEdgeY = TEMPLATE_SIZE.height - input.bottom;
  // Выше безопасной зоны заголовок уезжать не должен: там шапка рилса.
  const availableHeight = Math.max(1, bottomEdgeY - TEMPLATE_BOUNDS.top);

  // Отступы считаем ровно как drawOverlay — те же поля, та же формула, тот же
  // scaleX. Иначе перенос считается по одной внутренней ширине, а бокс рисуется
  // по другой, и текст вылезает за его край на узком окне.
  const padTop = (input.textBoxPaddingTop ?? input.textBoxPadding ?? 12) * scaleX;
  const padLeft = (input.textBoxPaddingLeft ?? input.textBoxPadding ?? 12) * scaleX;
  const padRight = (input.textBoxPaddingRight ?? input.textBoxPadding ?? 12) * scaleX;

  /** Разложить текст данным кеглем (в template px) — с двухпроходным уголком. */
  const layout = (fontPx: number) => {
    const lineHeight = fontPx * spacing;
    const font = buildFontString(fontPx, input.fontFamily, input.fontVariationSettings);

    const wrap = (availableWidth: number) => {
      const out: string[] = [];
      const maxLineWidth = availableWidth - padLeft - padRight;
      // Схлопываем повторные пробелы до поиска границы: строки склеиваются
      // одиночными пробелами, и только тогда смещение символа переносится в
      // (номер строки, смещение внутри неё) один в один.
      const segments = displayText.split('\n').map((p) => p.split(' ').filter(Boolean).join(' '));
      const boundary = resolveColorBoundary(segments.join('\n'));
      let consumed = 0;
      let primaryLines: number | null = null;
      let splitChars = 0;

      const take = (line: string) => {
        if (boundary !== null && primaryLines === null && boundary <= consumed + line.length) {
          primaryLines = out.length;
          splitChars = Math.max(0, boundary - consumed);
        }
        out.push(line);
        // +1 — пробел или перенос, съеденный при разбиении.
        consumed += line.length + 1;
      };

      for (const segment of segments) {
        if (segment === '') {
          take('');
          continue;
        }
        const words = segment.split(' ');
        let line = '';
        for (let i = 0; i < words.length; i++) {
          const testLine = line + words[i] + ' ';
          if (measure(testLine, font) > maxLineWidth && i > 0) {
            take(line.trim());
            line = words[i] + ' ';
          } else {
            line = testLine;
          }
        }
        if (line) take(line.trim());
      }
      // Границы нет — прежнее поведение: красится ровно первая строка.
      return {
        lines: out,
        primaryLines: primaryLines ?? 1,
        splitChars: primaryLines === null ? 0 : splitChars,
      };
    };

    const boxHeightFor = (n: number) => n * lineHeight + padTop * 2;

    // Two-pass notch: wrap full width, derive top from bottom + height, pick width, re-wrap.
    let availableWidth = FULL_WIDTH;
    let wrapped = wrap(availableWidth);
    const textY = bottomEdgeY - boxHeightFor(wrapped.lines.length);
    const refined = textY < TEMPLATE_BOUNDS.rightCornerTop ? FULL_WIDTH : NOTCH_WIDTH;
    if (refined !== availableWidth) {
      availableWidth = refined;
      wrapped = wrap(availableWidth);
    }

    let maxWidth = 0;
    for (const l of wrapped.lines) maxWidth = Math.max(maxWidth, measure(l, font));
    return {
      lines: wrapped.lines,
      primaryLines: wrapped.primaryLines,
      splitChars: wrapped.splitChars,
      textBoxWidth: maxWidth + padLeft + padRight,
      boxHeight: boxHeightFor(wrapped.lines.length),
    };
  };

  // Размер задан вручную — уважаем. Не задан — подбираем сами.
  //
  // Коридор задан в template px (кадр 1080 шириной), а не в preview px, и это
  // принципиально: preview-размер домножается на scaleX = 1080 / ширина_превью,
  // то есть один и тот же кегль давал разный рилс при разной ширине окна
  // браузера. Коридор тот же, что у постов, — ширина кадра совпадает, поэтому
  // пост и рилс одного паблика получают одинаковый заголовок.
  let fontPx: number;
  let result: ReturnType<typeof layout>;
  if (input.fontSize != null && input.fontSize > 0) {
    fontPx = input.fontSize * scaleX;
    result = layout(fontPx);
  } else {
    // По-шаблонный потолок заменяет общий — и вниз, и вверх (kaztwitter просит
    // крупнее 72). Пол коридора не выше потолка.
    const autoMax = Math.max(1, input.maxFontSize ?? AUTO_FONT_RANGE.max);
    const autoMin = Math.min(AUTO_FONT_RANGE.min, autoMax);
    fontPx = autoMax;
    result = layout(fontPx);
    if (result.boxHeight > availableHeight) {
      let lo = autoMin;
      let hi = autoMax;
      let best = layout(lo);
      let bestPx = lo;
      for (let i = 0; i < 12 && hi - lo > 0.5; i++) {
        const mid = (lo + hi) / 2;
        const at = layout(mid);
        if (at.boxHeight <= availableHeight) { best = at; bestPx = mid; lo = mid; } else { hi = mid; }
      }
      fontPx = bestPx;
      result = best;
    }
    // Правило трёх строк: плашка растёт пропорционально — вширь, а не башней.
    // Максимальный кегль складывал длинный заголовок в 5 узких строк, а медиа
    // на примере qaznews просит наоборот: «можно и 3 строки сделать, шире».
    // Поэтому при видимой плашке снижаем кегль (не ниже пола коридора), пока
    // строк не станет ≤ 3. Если и на полу строк больше — целимся в их число.
    if (input.plateVisible && result.lines.length > 3) {
      const floorLayout = layout(autoMin);
      const target = Math.max(3, floorLayout.lines.length);
      if (result.lines.length > target) {
        let lo = autoMin;
        let hi = fontPx;
        let best = floorLayout;
        let bestPx = autoMin;
        for (let i = 0; i < 12 && hi - lo > 0.5; i++) {
          const mid = (lo + hi) / 2;
          const at = layout(mid);
          if (at.lines.length <= target && at.boxHeight <= availableHeight) {
            best = at; bestPx = mid; lo = mid;
          } else { hi = mid; }
        }
        fontPx = bestPx;
        result = best;
      }
    }
  }

  return {
    lines: result.lines,
    primaryLines: result.primaryLines,
    splitChars: result.splitChars,
    // Наружу отдаём в preview-space: в этих единицах живут кнопки размера.
    fontSize: fontPx / scaleX,
    textBoxWidth: result.textBoxWidth,
  };
}
